// @flow strict
import * as React from 'react'
import {
  Button,
  ResponsiveImage,
  prefixHttps,
  TrackEvents,
} from '@mondough/public-web-components'
import cx from 'classnames'

import styles from './HeroSection.module.scss'

import { MarkdownContent, ContentThemes } from './MarkdownContent'
import { SectionWrapper, SectionThemes } from './SectionWrapper'

type ImageUrls = { desktop: string, mobile?: ?string }

type ImageAlts = { desktop: string, mobile?: string }

export type HeroSectionProps = {|
  title: string,
  description: string,
  cta: {|
    text: string,
    href: string,
    category?: string,
    action?: string,
    label?: string,
  |},
  imageUrls: ImageUrls,
  imageAlts?: ImageAlts,
  flags?: { [k: string]: string | number },
  heroDescription?: string,
|}

export function buildMobileImageSources(imageUrl?: ?string) {
  return (
    (imageUrl && [
      {
        srcSet: [
          `${prefixHttps(imageUrl)}?w=375&fm=webp&q=90 375w`,
          `${prefixHttps(imageUrl)}?w=750&fm=webp&q=90 750w`,
          `${prefixHttps(imageUrl)}?w=768&fm=webp&q=90 768w`,
          `${prefixHttps(imageUrl)}?w=1536&fm=webp&q=90 1536w`,
        ].join(','),
        media: '(max-width: 47.9375em)',
        sizes: '100vw',
        type: 'image/webp',
      },
      {
        srcSet: [
          `${prefixHttps(imageUrl)}?w=375&q=75 375w`,
          `${prefixHttps(imageUrl)}?w=750&q=75 750w`,
          `${prefixHttps(imageUrl)}?w=768&q=75 768w`,
          `${prefixHttps(imageUrl)}?w=1536&q=75 1536w`,
        ].join(','),
        media: '(max-width: 47.9375em)',
        sizes: '100vw',
      },
    ]) ||
    []
  )
}

export function buildHeroImageSources(
  imageUrls: ImageUrls,
  imageWidth?: string,
) {
  const smallerWidth = imageWidth || 1440
  const { mobile, desktop } = imageUrls
  const desktopSources = [
    {
      srcSet: [
        `${prefixHttps(
          desktop,
        )}?w=${smallerWidth}&fit=fill&f=left&fm=webp&q=90 1440w`,
        `${prefixHttps(desktop)}?w=2160&fit=fill&f=left&fm=webp&q=90 2160w`,
      ].join(','),
      media: '(min-width: 48em) ',
      sizes: '50vw',
      type: 'image/webp',
    },
    {
      srcSet: [
        `${prefixHttps(desktop)}?w=${smallerWidth}&fit=fill&f=left&q=75 1440w`,
        `${prefixHttps(desktop)}?w=2160&fit=fill&f=left&q=75 2160w`,
      ].join(','),
      media: '(min-width: 48em) ',
      sizes: '50vw',
      type: 'image/png',
    },
  ]

  const mobileSources = buildMobileImageSources(mobile)

  return [...desktopSources, ...mobileSources]
}

export function buildDefaultImage(
  imageUrls: ImageUrls,
  defaultWidth?: string = '1080',
) {
  if (imageUrls && imageUrls.mobile) {
    return `${prefixHttps(imageUrls.mobile)}?w=750&q=75`
  }

  return `${prefixHttps(
    imageUrls.desktop,
  )}?w=${defaultWidth}&fit=fill&f=left&h=840&q=75`
}

const HeroSection = ({
  title,
  description,
  imageUrls,
  imageAlts,
  flags,
  cta: { href, text, category, action, label },
}: HeroSectionProps) => {
  const isCentered = flags && flags.centered
  const heroImageSizes = imageUrls && buildHeroImageSources(imageUrls)
  return (
    <SectionWrapper
      className={cx(styles.section, {
        [styles.isCentered]: isCentered === true,
      })}
      contentClassName={styles.hero}
      theme={SectionThemes.DARK}
    >
      <div className={styles.content} data-testid="HeroSection">
        <div className={styles.text}>
          <h1 className={styles.title}>{title}</h1>
          <MarkdownContent
            className={styles.subtitle}
            theme={ContentThemes.DARK}
          >
            {description}
          </MarkdownContent>
          {href != null && text != null && (
            <div className={styles.cta}>
              <TrackEvents category={category} action={action} label={label}>
                <Button href={href}>{text}</Button>
              </TrackEvents>
            </div>
          )}
        </div>
      </div>
      <ResponsiveImage
        className={styles.image}
        defaultImage={buildDefaultImage(imageUrls)}
        sources={heroImageSizes}
      />
    </SectionWrapper>
  )
}

export default HeroSection
